import { defineStore, acceptHMRUpdate } from 'pinia'
import archivesService from '@/services/archives.service'

export interface ArchivesStoreState {
  discoverElectronicSafeDialogIsOpen: boolean
  listSearchPending: boolean
  listSearchData: Array<ArchiveDocument>
  listSearchDataCount: number
  config: ArchiveConfig | null
  configPending: boolean
  getArchiveRightsPending: boolean
  currentArchiveRights: ArchiveRights | Record<string, never>
  sendArchivesPending: boolean
  shareArchivesPending: boolean
  muteArchivingWarning: boolean
  uploadArchivePendingIds: Array<number>
  uploadArchiveErrors: Array<Error>
  postArchivePendingIds: Array<number>
  postArchiveErrors: Array<Error>
  selectedDocuments: Array<ArchiveDocument>
  archivesDownloadPending: boolean
}

export const useArchivesStore = defineStore('archives', {
  state: (): ArchivesStoreState => ({
    discoverElectronicSafeDialogIsOpen: false,
    listSearchPending: false,
    listSearchData: [],
    listSearchDataCount: 0,
    config: null,
    configPending: false,
    getArchiveRightsPending: false,
    currentArchiveRights: {},
    sendArchivesPending: false,
    shareArchivesPending: false,
    muteArchivingWarning: false,
    uploadArchivePendingIds: [],
    uploadArchiveErrors: [],
    postArchivePendingIds: [],
    postArchiveErrors: [],
    selectedDocuments: [],
    archivesDownloadPending: false,
  }),

  getters: {
    metadata: (state) => state.config?.metadata,
    sections: (state) => state.config?.sections,
    selectedDocumentsSize: (state) => state.selectedDocuments.reduce((accSum, item) => accSum + item.docSize, 0),
  },

  actions: {
    setDiscoverElectronicSafeDialogIsOpen(value: boolean) {
      this.discoverElectronicSafeDialogIsOpen = value
    },

    setMuteArchivingWarning(value: boolean) {
      this.muteArchivingWarning = value
    },

    removeDocument(index: number) {
      this.selectedDocuments.splice(index, 1)
    },

    async getSearchArchives({ search, params }: { search: string, params: any }) {
      this.listSearchPending = true
      try {
        const { data } = await archivesService.searchArchives(search, params)
        this.listSearchData = data.data
        this.listSearchDataCount = data.pagination.itemCount
      } finally {
        this.listSearchPending = false
      }
    },

    async getArchiveConfig() {
      if (!this.config) {
        this.configPending = true
        try {
          const { data } = await archivesService.getConfig()
          this.config = data
        } finally {
          this.configPending = false
        }
      }
    },

    async getArchiveRights(archiveId: number) {
      this.getArchiveRightsPending = true
      try {
        const { data } = await archivesService.getArchiveRights(archiveId)
        this.currentArchiveRights = data
      } finally {
        this.getArchiveRightsPending = false
      }
    },

    async shareArchives(file: any) {
      this.shareArchivesPending = true
      try {
        await archivesService.shareArchives(file)
      } finally {
        this.shareArchivesPending = false
      }
    },

    async uploadArchive(file: any) {
      const id = this.uploadArchivePendingIds.length + 1
      this.uploadArchivePendingIds.push(id)
      
      try {
        await archivesService.postArchive(file)
        file.status = 'success'
      } catch (error: any) {
        this.uploadArchiveErrors.push(error)
        file.status = 'error'
      } finally {
        const index = this.uploadArchivePendingIds.indexOf(id)
        if (index > -1) {
          this.uploadArchivePendingIds.splice(index, 1)
        }
      }
    },

    async postArchive(file: any) {
      this.postArchivePendingIds.push(file.id)
      try {
        await archivesService.postArchive(file)
        file.status = 'success'
      } catch (error: any) {
        file.status = 'error'
        this.postArchiveErrors.push(error)
      } finally {
        const index = this.postArchivePendingIds.indexOf(file.id)
        if (index > -1) {
          this.postArchivePendingIds.splice(index, 1)
        }
      }
    },

    selectDocuments(selectedDocuments: Array<ArchiveDocument>) {
      this.selectedDocuments = selectedDocuments
    },

    deselectAllDocuments() {
      this.selectedDocuments = []
    },

    async getArchivesDownload({ params }: { params: any }) {
      this.archivesDownloadPending = true
      try {
        const { data } = await archivesService.getArchivesDownload({ params })
        return data
      } finally {
        this.archivesDownloadPending = false
      }
    },

    async sendArchives({ params }: { params: any }) {
      this.sendArchivesPending = true
      try {
        await archivesService.sendArchives(params)
      } finally {
        this.sendArchivesPending = false
      }
    },
    
    // Mutation-like actions for managing lists
    addUploadArchiveError(error: Error) {
      this.uploadArchiveErrors.push(error)
    },
    
    removeUploadArchiveError(index: number) {
      this.uploadArchiveErrors.splice(index, 1)
    },
    
    addPostArchiveError(error: Error) {
      this.postArchiveErrors.push(error)
    },
    
    removePostArchiveError(index: number) {
      this.postArchiveErrors.splice(index, 1)
    }
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useArchivesStore, import.meta.hot))
}
